import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, ExternalLink } from 'lucide-react';
import { Card, PageHeader, TableSkeleton } from '../../components/ui';
import { Toast } from '../../components/ui/Alert';
import { EmailWorkflowApi, WorkflowRow, WorkflowScope } from '../../services/emailWorkflowApi';
import { ProcurementApiService, PendingApprovalLead } from '../../services/procurementApi';
import { formatINR } from '../../utils/currencyFormatter';
import { ApprovalBadge, FinanceBadge } from './badges';
import { WorkflowDetailBody } from './WorkflowDetailBody';

/**
 * Phase 3 — the unified Approvals inbox (docs/plan-approvals-restructure.md).
 * One "Pending my approval" list across sources, normalized to
 * {source, title, amount, city, date}. Two maps drive it:
 *   render map: payment → WorkflowDetailBody inline; procurement → LINK OUT
 *   (VendorInvoiceApprovals.tsx is FROZEN — we reuse only procurementApi).
 * Chips are business categories derived client-side from workflow_type.
 * URL-ONLY for now (/approvals/inbox, no menu seed) — parallel rollout.
 */

type Chip = 'Procurement' | 'Ops' | 'Reimbursement';
interface InboxRow {
	key: string;
	source: 'payment' | 'procurement';
	chip: Chip;
	title: string;
	sub: string;
	city: string | null;
	amount: number | null;
	date: string | null;
	threadId?: string; // payment rows expand inline
	row?: WorkflowRow;
}

const chipOfPayment = (r: WorkflowRow): Chip => (r.workflow_type === 'reimbursement' ? 'Reimbursement' : 'Ops');
const num = (v: unknown): number | null => {
	const n = Number(v);
	return v != null && Number.isFinite(n) ? n : null;
};
const fmtDate = (iso: string | null) => {
	if (!iso) return '—';
	const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
	return m ? `${m[3]}/${m[2]}/${m[1].slice(2)}` : iso;
};

const CHIP_STYLE: Record<Chip, string> = {
	Procurement: 'bg-purple-50 text-purple-700 border-purple-200',
	Ops: 'bg-blue-50 text-blue-700 border-blue-200',
	Reimbursement: 'bg-teal-50 text-teal-700 border-teal-200',
};

export const ApprovalsInbox: React.FC = () => {
	const navigate = useNavigate();
	const [mode, setMode] = useState<'pending' | 'history'>('pending');
	const [chip, setChip] = useState<Chip | null>(null);
	const [payments, setPayments] = useState<WorkflowRow[]>([]);
	const [procurement, setProcurement] = useState<PendingApprovalLead[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [reloadKey, setReloadKey] = useState(0);
	const [openThreadId, setOpenThreadId] = useState<string | null>(null);
	const [toast, setToast] = useState<string | null>(null);
	const [scope, setScope] = useState<WorkflowScope | null>(null);

	useEffect(() => {
		EmailWorkflowApi.getMe().then(setScope).catch(() => setScope(null));
	}, []);

	useEffect(() => {
		let cancelled = false;
		setLoading(true);
		setError(null);
		const paymentsP = EmailWorkflowApi.getBoard({ view: mode, page_size: 200 })
			.then(r => r.rows)
			.catch(() => [] as WorkflowRow[]);
		// per-user map: procurement queue only exists on the pending side (history
		// needs a backend acted_by=me param — deferred). Errors = "no queue".
		const procP =
			mode === 'pending'
				? ProcurementApiService.getMyPendingApprovals(1, 50)
						.then(r => (r.status ? r.data : []))
						.catch(() => [] as PendingApprovalLead[])
				: Promise.resolve([] as PendingApprovalLead[]);
		Promise.all([paymentsP, procP])
			.then(([pay, proc]) => {
				if (cancelled) return;
				setPayments(pay);
				setProcurement(proc);
			})
			.catch(() => !cancelled && setError('Could not load the inbox.'))
			.finally(() => !cancelled && setLoading(false));
		return () => {
			cancelled = true;
		};
	}, [mode, reloadKey]);

	const allRows = useMemo<InboxRow[]>(() => {
		// pending = only MY seat's turn (server's view=pending over-returns; contract v2 §3)
		const seats = scope?.approver_at ?? [];
		const myTurnRows =
			mode === 'pending' && scope != null
				? payments.filter(
						r =>
							r.facility_key == null ||
							r.current_tier_role == null ||
							seats.some(s => s.facility_key === r.facility_key && s.role === r.current_tier_role)
					)
				: payments;
		const pay: InboxRow[] = myTurnRows.map(r => ({
			key: `pay-${r.thread_id}`,
			source: 'payment',
			chip: chipOfPayment(r),
			title: r.counterparty || 'Unknown payee',
			sub: r.gist,
			city: r.city,
			amount: num(r.attachment_amount) ?? num(r.email_claimed_amount),
			date: r.request_date ?? r.created_at,
			threadId: r.thread_id,
			row: r,
		}));
		const proc: InboxRow[] = procurement.map(l => ({
			key: `proc-${l.leadId}`,
			source: 'procurement',
			chip: 'Procurement',
			title: l.client,
			sub: `${l.vendors.length} vendor invoice${l.vendors.length === 1 ? '' : 's'} to approve`,
			city: l.city,
			amount: l.totalVendorCost ?? l.vendors.reduce((s, v) => s + (v.invoiceAmount ?? 0), 0) ?? null,
			date: l.vendors[0]?.approvalSubmittedAt ?? null,
		}));
		return [...proc, ...pay];
	}, [payments, procurement, scope, mode]);

	const rows = useMemo(() => (chip ? allRows.filter(r => r.chip === chip) : allRows), [allRows, chip]);

	const counts = useMemo(() => {
		const c: Record<Chip, number> = { Procurement: 0, Ops: 0, Reimbursement: 0 };
		allRows.forEach(r => {
			c[r.chip] += 1;
		});
		return c;
	}, [allRows]);

	return (
		<div className="p-6">
			<PageHeader title="Approvals Inbox" totalItems={rows.length} itemType="items" icon="📥" />

			{/* pending | history */}
			<div className="mb-3 inline-flex rounded-lg border border-gray-200 bg-gray-50 p-0.5" role="group" aria-label="Inbox view">
				{([
					['pending', 'Pending my approval'],
					['history', 'Approved by me'],
				] as const).map(([id, label]) => (
					<button
						key={id}
						type="button"
						onClick={() => {
							setMode(id);
							setOpenThreadId(null);
						}}
						aria-pressed={mode === id}
						className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
							mode === id ? 'bg-white text-primary shadow-sm' : 'text-gray-500 hover:text-gray-700'
						}`}
					>
						{label}
					</button>
				))}
			</div>

			{/* source chips */}
			<div className="mb-4 flex flex-wrap gap-2">
				{(['Procurement', 'Ops', 'Reimbursement'] as Chip[]).map(c => (
					<button
						key={c}
						type="button"
						onClick={() => setChip(chip === c ? null : c)}
						aria-pressed={chip === c}
						className={`px-3 py-1 rounded-full border text-xs font-medium transition-colors ${
							chip === c ? CHIP_STYLE[c] : 'border-gray-200 text-gray-500 hover:text-gray-700'
						}`}
					>
						{c} · {counts[c]}
					</button>
				))}
				{mode === 'history' && (
					<span className="text-xs text-gray-400 self-center">History covers payment requests for now.</span>
				)}
			</div>

			{loading && (payments.length === 0 && procurement.length === 0) && <TableSkeleton />}

			{!loading && error && (
				<Card className="text-center py-10">
					<p className="text-red-600 font-medium">{error}</p>
					<button type="button" onClick={() => setReloadKey(k => k + 1)} className="mt-3 text-sm text-primary underline">
						Try again
					</button>
				</Card>
			)}

			{!loading && !error && rows.length === 0 && (
				<Card className="text-center py-12">
					<div className="text-4xl mb-3">🎉</div>
					<p className="text-gray-800 font-medium">
						{mode === 'pending' ? 'Nothing waiting for you' : 'No history yet'}
					</p>
					<p className="text-sm text-gray-500 mt-1 max-w-md mx-auto">
						{mode === 'pending'
							? 'Requests and invoices that need your approval will show up here.'
							: 'Items you have acted on will be listed here.'}
					</p>
				</Card>
			)}

			{!error && rows.length > 0 && (
				<Card className={loading ? 'opacity-60 transition-opacity' : 'transition-opacity'}>
					<div className="overflow-x-auto">
						<table className="w-full text-[13px]">
							<thead>
								<tr className="border-b border-gray-200 text-left text-gray-500">
									<th className="py-2 pr-2 font-medium text-gray-400">#</th>
									<th className="py-2 pr-3 font-medium">Source</th>
									<th className="py-2 pr-3 font-medium">What</th>
									<th className="py-2 pr-3 font-medium">City</th>
									<th className="py-2 pr-3 font-medium text-right">Amount</th>
									<th className="py-2 pr-3 font-medium">Status</th>
									<th className="py-2 font-medium text-right">Date</th>
								</tr>
							</thead>
							<tbody>
								{rows.map((r, i) => {
									const expanded = openThreadId != null && openThreadId === r.threadId;
									return (
										<React.Fragment key={r.key}>
											<tr
												onClick={() =>
													r.source === 'procurement'
														? navigate('/approvals/vendor-invoices') // FROZEN page — link out, never inline
														: setOpenThreadId(expanded ? null : (r.threadId ?? null))
												}
												aria-expanded={r.source === 'payment' ? expanded : undefined}
												className={`border-b border-gray-100 last:border-0 hover:bg-gray-50 cursor-pointer ${expanded ? 'bg-primary/5' : ''}`}
											>
												<td className="py-2 pr-2 text-gray-400 tabular-nums">{i + 1}</td>
												<td className="py-2 pr-3">
													<span className={`inline-block px-2 py-0.5 rounded-full border text-xs font-medium ${CHIP_STYLE[r.chip]}`}>
														{r.chip}
													</span>
												</td>
												<td className="py-2 pr-3 max-w-[280px]">
													<div className="flex items-center gap-1.5">
														{r.source === 'payment' ? (
															<ChevronRight className={`h-3.5 w-3.5 shrink-0 text-gray-400 transition-transform ${expanded ? 'rotate-90' : ''}`} />
														) : (
															<ExternalLink className="h-3.5 w-3.5 shrink-0 text-gray-400" aria-label="Opens Vendor Invoice Approvals" />
														)}
														<span className="text-gray-900 truncate">{r.title}</span>
													</div>
													<div className="text-gray-400 truncate text-xs pl-5">{r.sub}</div>
												</td>
												<td className="py-2 pr-3">{r.city || <span className="text-gray-400">—</span>}</td>
												<td className="py-2 pr-3 text-right tabular-nums text-gray-900">
													{r.amount != null ? formatINR(r.amount) : '—'}
												</td>
												<td className="py-2 pr-3">
													{r.row ? (
														<span className="inline-flex gap-1.5">
															<ApprovalBadge status={r.row.effective_approval_status} source={r.row.effective_approval_source} />
															<FinanceBadge status={r.row.finance_status} />
														</span>
													) : (
														<span className="text-gray-400 text-xs">on the invoice page</span>
													)}
												</td>
												<td className="py-2 text-right text-gray-500 tabular-nums">{fmtDate(r.date)}</td>
											</tr>
											{expanded && r.threadId && (
												<tr className="border-b border-gray-100 last:border-0">
													<td colSpan={7} className="bg-gray-50/70 px-4 py-4">
														<WorkflowDetailBody
															threadId={r.threadId}
															onChanged={msg => {
																setReloadKey(k => k + 1);
																if (msg) setToast(msg); // row leaves the pending list on approve
															}}
															financeActions
														/>
													</td>
												</tr>
											)}
										</React.Fragment>
									);
								})}
							</tbody>
						</table>
					</div>
				</Card>
			)}

			{toast && (
				<Toast variant="success" position="top-center" duration={5000} onClose={() => setToast(null)}>
					{toast}
				</Toast>
			)}
		</div>
	);
};

export default ApprovalsInbox;
